import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { Dialog, useDialog } from 'components';
import ControlledDataTable from 'components/tables/controlled-data-table';
import { DataTableRef } from 'components/tables/data-table';
import { ControlledDataTableColumn, ControlledDataTableProvider } from 'components/tables/data-table-context';
import { useSelectedLocation } from 'contexts';
import { useResponse } from 'contexts/response';
import { useFetch } from 'hooks';
import { savedMessage } from 'pages/admin/customers/edit/account-link';
import { FleetFile } from 'types';
import { LogicalOperator } from 'utils';

import { AccountLinkForm } from './account-link-form';
import { RowEditButton } from './row-edit-button';

const CustomerMappings: FC = () => {
  const navigate = useNavigate();
  const { selectedLocation } = useSelectedLocation();
  const { setSuccess } = useResponse();
  const { fetchPaginated } = useFetch<FleetFile.Customer>('fleet-files/customers');
  const { ref, openDialog, closeDialog } = useDialog();
  const tableRef = useRef<DataTableRef>(null);
  /* the account being edited in the dialog, if any */
  const [editing, setEditing] = useState<number>();

  const edit = useCallback(
    (customerId: number) => {
      setEditing(customerId);
      openDialog();
    },
    [openDialog]
  );

  const close = useCallback(() => {
    closeDialog();
    setEditing(undefined);
  }, [closeDialog]);

  const onSaved = useCallback(
    (code: number | null) => {
      close();
      setSuccess(savedMessage(code));
      tableRef.current?.forceFetch();
    },
    [close, setSuccess]
  );

  const sizeTypeColumns: ControlledDataTableColumn<FleetFile.Customer>[] = useMemo(() => {
    return [
      {
        field: 'code',
        label: 'FLEET FILE CODE',
        type: 'number',
        /* the same row action as the size-type grid: edit this one account's link, in place */
        renderCell: (code: number, row: FleetFile.Customer) => {
          const account = row.customer;
          return account?.id ? (
            <RowEditButton label={`Edit fleet link for ${account.name}`} onClick={() => edit(account.id)}>
              {code}
            </RowEditButton>
          ) : (
            code
          );
        }
      },
      { field: 'name', label: 'FLEET FILE NAME', type: 'string' },
      { field: 'customer', label: 'ACCOUNT', type: 'customer', getValueLabel: customer => customer?.name ?? '' },
      { field: 'location', label: 'LOCATION', type: 'location', getValueLabel: location => location?.name ?? '' }
    ];
  }, [edit]);

  const initialColumns: string[] = useMemo(() => sizeTypeColumns.map(({ field }) => field), [sizeTypeColumns]);

  return (
    <div className="card-primary d-flex flex-fill flex-column overflow-hidden" style={{ marginTop: 0, minHeight: 320 }}>
      <Dialog ref={ref} closeDialog={close}>
        {editing !== undefined && (
          <AccountLinkForm key={editing} customerId={editing} onSaved={onSaved} onCancel={close} />
        )}
      </Dialog>
      <div className="card-header p-relative">
        <h4 className="p-absolute" style={{ top: 0, left: '50%', translate: '-50%' }}>
          ACCOUNT MAPPINGS
        </h4>
        <button
          className="btn-primary grid-1 py-2 px-4 p-absolute"
          style={{ top: 4, right: 12 }}
          disabled={!selectedLocation}
          onClick={() => navigate(`/settings/fleet-files/new?location=${selectedLocation?.id}`)}
        >
          {/* text, not a pencil: this edits every link at the location, and the pencil means one row */}
          BULK EDIT LINKS
        </button>
      </div>
      <div className="card-body d-flex flex-column flex-fill overflow-hidden">
        <ControlledDataTableProvider initialColumns={initialColumns} initialPagination={{ sortField: 'code' }}>
          <ControlledDataTable<FleetFile.Customer>
            columns={sizeTypeColumns}
            dataTableRef={tableRef}
            fetchData={fetchPaginated}
            filterByLocation
            searchLogicalOperator={LogicalOperator.AND}
            tableToolbar
          />
        </ControlledDataTableProvider>
      </div>
    </div>
  );
};

export default CustomerMappings;
